import {
  API_BASE_URL,
  API_SIMULATION_URL,
  ELECTRICITY_OFFER_TYPE,
  GAS_OFFER_TYPE,
  isAdvanced,
  isSimple,
  isEnterprise
} from 'config'

import axios from 'axios'
import PropTypes from 'prop-types'
import moment from 'moment'

/** Redux and Kea */
import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'
import { delay } from 'redux-saga'

import DataLogic from 'containers/App/logic'
import FiltersLogic from 'containers/Filters/logic'

import omit from 'lodash/omit'
import pick from 'lodash/pick'

import { csObjectToUnderscore, csObjectFromUnderscore, validNumber } from 'utils'

/** Contants */
const SIMULATOR_SUGESTIONS_ENDPOINT = `${API_BASE_URL}/public/simulator/simulation_price_list_suggestion/`
const PRICELIST_ENDPOINT = `${API_BASE_URL}/public/price-list/`
const UPDATE_PRICES_ENDPOINT = `${API_SIMULATION_URL}/public/simulation/:code/`
const SIMULATION_PROFILE_ENDPOINT = `${API_BASE_URL}/private/simulation-user-profile/`
const ME_ENDPOINT = `${API_BASE_URL}/private/user-profile/me/`
const AUTH_TOKEN_KEY = `tokenauth`

// Simulator url changes depending on the type of the simulator
let SIMULATOR_ENPOINT = `${API_SIMULATION_URL}/public/simulator/advanced/`

if (isSimple) {
  SIMULATOR_ENPOINT = `${API_SIMULATION_URL}/public/simulator/simple/`
}

if (isEnterprise) {
  SIMULATOR_ENPOINT = `${API_SIMULATION_URL}/public/simulator/enterprise/`
}

const BILLING_DICT = {
  dd: 'direct_debit',
  mb: 'atm_ctt',
  fe: 'electronic_billing',
  fp: 'paper_billing',
  ce: 'electronic_contract'
}

const EXTRA_DICT = {
  ge: 'green_energy',
  rg: 'is_regulated',
  as: 'no_additional_services',
  hr: 'has_refunds'
}

// const DURATION_DICT = {
//   no: 'no_contract_duration',
//   m6: 'contract_duration_6_months',
//   m12: 'contract_duration_12_months',
//   m24: 'contract_duration_24_months'
// }

const convertArray = (filters, key, dict) => {
  const obj = {}
  filters[key].forEach(val => {
    if (val !== 'any') {
      obj[dict[val]] = true
    }
  })

  return obj
}

const convertBilling = (filters) => {
  return convertArray(filters, 'billing', BILLING_DICT)
}

const convertExtra = (filters) => {
  return convertArray(filters, 'extra', EXTRA_DICT)
}

const formatEquipmentsForRequest = (equipments) => {
  return equipments &&
    equipments.map(({ id, quantity, selectedClass }) => ({
      equipment: id,
      equipment_profile_consumption: selectedClass.id,
      quantity
    }))
}

const formatElectricityTableItems = (item) => ({
  start_date: moment(item.startDate).format('YYYY-MM-DD'),
  end_date: moment(item.endDate).format('YYYY-MM-DD'),
  s: validNumber(item.simples),
  f: validNumber(item.svazio),
  c: validNumber(item.cheias),
  p: validNumber(item.ponta)
})

const formatGasTableItems = ({ startDate, endDate, consumption }) => ({
  start_date: moment(startDate).format('YYYY-MM-DD'),
  end_date: moment(endDate).format('YYYY-MM-DD'),
  consumption: validNumber(consumption)
})

export default kea({
  path: () => ['scenes', 'results'],

  connect: {
    actions: [DataLogic, ['setValue', 'setValues', 'setFinished', 'setIsUserLoggedIn', 'iframed'],
      FiltersLogic, [
        'triggerFilter'
      ]
    ],
    props: [DataLogic, ['values', 'translations', 'finished', 'settings'],
      FiltersLogic, [
        'filters'
      ]
    ]
  },

  actions: () => ({
    makeSimulation: () => ({ }),
    setSimulationResults: (results) => ({ results }),
    setSimulationSugestions: (results) => ({ ...results }),
    changePage: (params) => ({ params }),
    setError: (error) => ({ ...error }),
    toggleEditPricesModal: (modalPrices) => ({ modalPrices }),
    toggleSaveProfileModal: (modalDetails) => ({ modalDetails }),
    saveProfile: (isNewFromUserWithProfiles) => ({ isNewFromUserWithProfiles }),
    updateProfile: (id, name) => ({ id, name }),

    editPrices: () => ({ }),
    setPrices: (prices) => ({ prices }),
    changePrices: (type, field, value) => ({ type, field, value }),
    updatePrices: () => ({ }),
    resetPrices: () => ({ }),

    setUserProfile: (profile) => ({ profile }),
    openFiltering: () => ({ }),
    closeFiltering: () => ({ }),
    
    search: (str) => ({ str }),
    filter: () => ({}),
    order: (order) => ({ order }),
    
    fetchUserProfile: (id, name) => ({ id, name }),
    fetchTariffInfo: (tariff) => ({ tariff }),
    setTariffInfo: (tariffInfo) => ({ tariffInfo }),
    setFetchError: (message) => ({ message }),
    setFilters: (filters) => ({ filters }),
    reset: () => ({}),

    getCurrentUser: () => ({})
  }),

  reducers: ({ actions }) => ({
    simulation: [{}, PropTypes.any, {
      [actions.setSimulationResults]: (_, payload) => ({ ...payload }),
      [actions.reset]: () => ({})
    }],

    search: [null, PropTypes.string, {
      [actions.search]: (_, payload) => payload.str
    }],

    ordering: ['asc', PropTypes.any, {
      [actions.order]: (_, p) => p.order.value
    }],

    sugestions: [{}, PropTypes.any, {
      [actions.setSimulationSugestions]: (_, payload) => ({ ...payload }),
      [actions.reset]: () => ({})
    }],

    currentPage: [1, PropTypes.any, {
      [actions.changePage]: (_, payload) => payload.params.currentPage
    }],

    simulating: [true, PropTypes.bool, {
      [actions.setSimulationResults]: () => false,
      [actions.makeSimulation]: () => true,
      [actions.setError]: () => false
    }],

    simulatingPatch: [false, PropTypes.bool, {
      [actions.setSimulationResults]: () => false,
      [actions.updatePrices]: () => true,
      [actions.setError]: () => false
    }],

    loadingSugested: [true, PropTypes.bool, {
      [actions.setSimulationSugestions]: () => false,
      [actions.setError]: () => false,
      [actions.changePage]: () => true,
      [actions.filter]: () => true,
      [actions.search]: () => true
    }],

    error: [false, PropTypes.bool, {
      [actions.setError]: (_, payload) => ({ ...payload.error }),
      [actions.makeSimulation]: () => false
    }],

    filtering: [false, PropTypes.bool, {
      [actions.openFiltering]: () => true,
      [actions.closeFiltering]: () => false
    }],

    tariffInfo: [null, PropTypes.object, {
      [actions.setTariffInfo]: (_, payload) => payload.tariffInfo,
      [actions.reset]: () => null
    }],

    showSaveProfileModal: [{ opened: false }, PropTypes.any, {
      [actions.toggleSaveProfileModal]: (state, payload) => ({...state, ...payload.modalDetails})
    }],

    showEditPricesModal: [{ opened: false }, PropTypes.any, {
      [actions.toggleEditPricesModal]: (state, payload) => ({...state, ...payload.modalPrices })
    }],

    startFilters: [null, PropTypes.any, {
      [actions.setFilters]: (state, payload) => payload.filters
    }],

    userProfile: [{}, PropTypes.any, {
      [actions.setUserProfile]: (state, payload) => payload.profile
    }],

    pricesForm: [{}, PropTypes.object, {
      [actions.setPrices]: (state, payload) => payload.prices,
      [actions.changePrices]: (state, payload) => {
        const type = payload.type
        // If no type: is a boolean toggle of field 'apply_refunds'
        if (!type) {
          return {
            ...state,
            [payload.field]: !state[payload.field]
          }
        } else {
          return { 
            ...state, 
            [type]: {
              ...state[type],
              [payload.field]: payload.value
            }
          }
        }
      }
    }]
  }),

  start: function * () {
    const {
      makeSimulation
    } = this.actions

    const { profileFinished } = yield DataLogic.get('finished')

    // https://sentry2.ubiwhere.com/ubiwhere/simulador-avancado/issues/3364/
    if (!profileFinished) {
      this.props.router.push('/')
    } else {
      yield put(makeSimulation())
    }
  },

  takeLatest: ({ actions, workers }) => ({
    [actions.makeSimulation]: workers.makeSimulation,
    [actions.setSimulationResults]: workers.fetchSugestions,
    [actions.changePage]: workers.fetchSugestions,
    [actions.search]: workers.fetchSugestions,
    [actions.filter]: workers.fetchSugestions,
    [actions.order]: workers.fetchSugestions,
    [actions.triggerFilter]: workers.triggerFiltering,
    [actions.fetchTariffInfo]: workers.fetchTariffInfo,
    [actions.saveProfile]: workers.saveProfile,
    [actions.updateProfile]: workers.updateProfile,
    [actions.editPrices]: workers.editPrices,
    [actions.updatePrices]: workers.updatePrices,
    [actions.getCurrentUser]: workers.getCurrentUser,
    [actions.getCurrentUser]: workers.getCurrentUser,
    [actions.fetchUserProfile]: workers.fetchUserProfile
  }),

  workers: {
    * fetchTariffInfo (action) {
      const { id } = action.payload.tariff
      const { setTariffInfo, setFetchError } = this.actions
      try {
        const info = yield call(axios.get, `${PRICELIST_ENDPOINT}${id}/`)
        info.data.priceListObject = action.payload.tariff
        yield put(setTariffInfo(info.data))
      } catch (error) {
        yield put(setFetchError(error))
      }
    },

    * makeSimulation () {
      const { setSimulationResults, setError, setFinished, reset, updateProfile, setFilters } = this.actions
      const settings = yield DataLogic.get('settings')

      yield put(reset())

      let rawValues = yield DataLogic.get('values')
      let offerType = yield DataLogic.get('offerType')
      let values = Object.assign({}, rawValues)

      let equipments = values.equipments

      // Sum habitants
      const { adults, children, elderly } = values
      values.habitants = (adults + children + elderly) || 0
      // Format table date to request
      if (isAdvanced || isEnterprise) {
        if (values.electricityConsumptionsRegistration) {
          values.electricityConsumptionsRegistration = [...values.electricityConsumptionsRegistration]
          .map(formatElectricityTableItems)
        }

        if (values.gasConsumptionsRegistration) {
          values.gasConsumptionsRegistration = [...values.gasConsumptionsRegistration]
          .map(formatGasTableItems)
        }
      }

      values.additionalData = csObjectToUnderscore(
        pick(values, [
          'key',
          'equipments',
          'adults',
          'children',
          'elderly',
          'district'
        ])
      )

      // Convert to undescore and omit uneeded values
      values = csObjectToUnderscore(
        omit(values, [
          'key',
          'equipments',
          'adults',
          'children',
          'elderly',
          'district',
          'first',
          'open'
        ])
      )

      // Omit entity field based in offer type
      if (offerType.slug === ELECTRICITY_OFFER_TYPE) {
        values = omit(values, ['gas_entity', 'gas_stage', 'gas_consumptions_registration'])
      }

      if (offerType.slug === GAS_OFFER_TYPE) {
        values = omit(values, [
          'electricity_entity',
          'electricity_consumptions_registration'
        ])
      }

      if (isEnterprise) {
        values.work_hours = omit(values.work_hours, [
          'id',
          'name'
        ])
      }

      // Format equipments
      values.equipment = formatEquipmentsForRequest(equipments)

      if (!values.hide_additional) {
        yield put(setFilters({ extra: ['as'] }))
      }

      try {
        const response = yield call(axios.post, SIMULATOR_ENPOINT, { ...values })
        const { data } = response
        yield put(setFinished({ simulationFinished: true, simulationError: false }))
        yield put(setSimulationResults(csObjectFromUnderscore(data)))

        // If is from a profile already loaded
        // Save the profile and open the modal
        yield delay(800)
        if (settings.isFromProfile) {
          yield put(updateProfile(settings.isFromProfile))
        }
      } catch (error) {
        let simulationErrorCode = 'error'

        if (typeof error.response.data.indexOf === 'function') {
          if (error.response.data.indexOf('Electric Price List is Empty') !== -1) {
            simulationErrorCode = 'electric'
          } else if (error.response.data.indexOf('Gas Price List is Empty') !== -1) {
            simulationErrorCode = 'gas'
          } else if (error.response.data.indexOf('Electronic Consumption Registration : Date Colisions, the start_date of an entry must be higher or equal to the previous end_date') !== -1) {
            simulationErrorCode = 'electric_registration'
          } else if (error.response.data.indexOf('Gas Consumption Registration : Date Colisions, the start_date of an entry must be higher or equal to the previous end_date') !== -1) {
            simulationErrorCode = 'gas_registration'
          }
        }

        yield put(setFinished({ simulationFinished: true, simulationError: true, simulationErrorCode }))
        yield put(setError(error))
      }
    },

    * fetchSugestions (action) {
      const { setSimulationSugestions, setError } = this.actions
      const { results } = yield this.get('simulation')
      const { code } = results
      const search = yield this.get('search')
      const ordering = yield this.get('ordering')
      const filters = yield FiltersLogic.get('filters')
      const actionsParams = action.payload.params ? action.payload.params : {}
      let order = 'annual_billing'

      if (ordering !== 'asc') {
        order = '-annual_billing'
      }
      let params = {
        limit: 10,
        ordering: order,
        is_active: true,
        ...actionsParams,
        ...filters
      }

      if (search) {
        params.entity_designation = search
      } else {
        params = omit(params, 'entity_designation')
      }

      // Convert arrays to multi filters
      if (params.billing) {
        Object.assign(params, convertBilling(params))
        delete params.billing
      }

      if (params.extra) {
        Object.assign(params, convertExtra(params))
        delete params.extra
      }

      // Caso o filtro: Sem Reembolsos esteja selecionado, o has_refunds não pode ir a true mas sim a false
      // GET https://teste.poupaenergia.pt/d/api/v1/public/price-list/?has_refunds=false
      if (params.has_refunds === true) {
        params.has_refunds = false
      }

      if (params.no_contract_duration) {
        if (params.no_contract_duration === 'any') {
          delete params.no_contract_duration
        }
      }

      try {
        const response = yield call(axios.get, SIMULATOR_SUGESTIONS_ENDPOINT, { params: { code, ...params } })
        const { data } = response

        data.results = data.results.map(item => csObjectFromUnderscore(item))

        yield put(setSimulationSugestions(data))
      } catch (error) {
        yield put(setError(error))
        yield put(setSimulationSugestions([]))
      }
    },

    /**
     * Triggers fetching when filtering
     * @param {Object} action Caller action
     */
    * triggerFiltering (action) {
      const { filter } = this.actions
      yield delay(100)
      yield put(filter())
    },

    /**
     * Saves the simulation profile to the user account
     * Flow: https://projects.invisionapp.com/d/main#/console/11390927/270514272/preview
     */
    * saveProfile (action) {
      const { toggleSaveProfileModal, setFetchError } = this.actions
      const userLoggedIn = yield DataLogic.get('userLoggedIn')
      const simulation = yield this.get('simulation')
      const isNewFromUserWithProfiles = action.payload.isNewFromUserWithProfiles
      let modalDetails = {
        loggedIn: !!userLoggedIn,
        opened: true
      }

      try {
        if (userLoggedIn) {
          const requestProfiles = yield call(axios.get, SIMULATION_PROFILE_ENDPOINT)
          const profiles = requestProfiles.data.results
          const hasProfiles = !!profiles.length

          modalDetails.profiles = profiles
          modalDetails.hasProfiles = hasProfiles

          // If user does not have profiles do save
          if (!hasProfiles || isNewFromUserWithProfiles) {
            const nextHouseNumber = profiles.length + 1
            const saveData = {
              name: `${isEnterprise ? 'Empresa' : 'Casa'} ${nextHouseNumber}`,
              simulation_history: simulation.results.code
            }
            const saveRequest = yield call(axios.post, SIMULATION_PROFILE_ENDPOINT, saveData)
            const saved = saveRequest.data

            modalDetails.saved = saved
            modalDetails.type = 'saved'
          } else {
            modalDetails.type = 'update'
          }
        } else {
          modalDetails.type = 'notlogged'
        }

        yield put(toggleSaveProfileModal(modalDetails))
      } catch (error) {
        // Do something here if any of the stuff above fails
        yield put(setFetchError(error))
      }
    },

    * updateProfile (action) {
      const { toggleSaveProfileModal, fetchUserProfile } = this.actions
      const id = action.payload.id
      const name = action.payload.name
      const simulation = yield this.get('simulation')
      const url = `${SIMULATION_PROFILE_ENDPOINT}${id}/`

      try {
        let request = {
          simulation_history: simulation.results.code
        }

        // If name exists update only the name
        if (name) {
          request = {
            name
          }
        }
        const saveRequest = yield call(axios.patch, url, request)
        yield put(toggleSaveProfileModal({ opened: true, type: 'updated', saved: saveRequest.data }))
        yield put(fetchUserProfile(id))
      } catch (error) {

      }
    },

    /**
     * If is simulation on user profile, get user house
     */
    * fetchUserProfile (action) {
      const { setUserProfile } = this.actions
      const { id } = action.payload
      const url = `${SIMULATION_PROFILE_ENDPOINT}${id}/`

      try {
        const request = yield call(axios.get, url)
        yield put(setUserProfile(request.data))
      } catch( error ) {
        console.log(error)
      }
    },
        /**
     * Gets current user logged in
     */
    * getCurrentUser () {
      const { setIsUserLoggedIn, saveProfile } = this.actions

      try {
        const token = window.localStorage.getItem(AUTH_TOKEN_KEY)

        if (!token) {
          yield put(setIsUserLoggedIn(false))
          return
        }

        axios.defaults.headers.common['Authorization'] = `JWT ${token}`

        const me = yield call(axios.get, ME_ENDPOINT)
        yield put(setIsUserLoggedIn(me.data))
        yield put(saveProfile())
      } catch (error) {
        yield put(setIsUserLoggedIn(false))
        delete axios.defaults.headers.common['Authorization']
      }
    },

    * editPrices () {
      const { setPrices, toggleEditPricesModal } = this.actions
      const simulationData = yield this.get('simulation')

      const simulation = simulationData.results
      
      let applyRefunds
      if (parseFloat(simulation.annualRefunds) !== 0.0) {
        applyRefunds = true
      } else {
        applyRefunds = false
      }

      let pricesObject = {}
      if (simulation) {
        // IF simulation is dual
        if (simulation.prices.electricity && simulation.prices.gas) {
          pricesObject = {
            electricity_prices: {
              ...simulation.prices.electricity,
            },
            gas_prices: {
              ...omit(simulation.prices.gas, ['distributor', 'escalao', 'stage'])
            },
            apply_refunds: applyRefunds,
            invalid: false
          }
        } 
        // If simulation is just electricity
        if (simulation.prices.electricity && !simulation.prices.gas) {
          pricesObject = {
            electricity_prices: {
              ...simulation.prices.electricity,
            },
            apply_refunds: applyRefunds,
            invalid: false
          }
        }
        // If simulation is just gas
        if (!simulation.prices.electricity && simulation.prices.gas) {
          pricesObject = {
            gas_prices: {
              ...omit(simulation.prices.gas, ['distributor', 'escalao', 'stage'])
            },
            apply_refunds: applyRefunds,
            invalid: false
          }
        }
      }

      yield put(setPrices(pricesObject))
      yield put(toggleEditPricesModal({ opened: true }))
    },

    * updatePrices () {
      const { setSimulationResults, toggleEditPricesModal, setError } = this.actions
      let form = yield this.get('pricesForm')
      const simulationData = yield this.get('simulation')

      // Pass to a valid number to send to API
      form.electricity_prices && Object.keys(form.electricity_prices).forEach(key => {
        form = { 
          ...form,
          electricity_prices: {
            ...form.electricity_prices,
            [key]: validNumber(form.electricity_prices[key]) 
          }
        }
      }) 

      form.gas_prices && Object.keys(form.gas_prices).forEach(key => {
        form = { 
          ...form,
          gas_prices: {
            ...form.gas_prices,
            [key]: validNumber(form.gas_prices[key]) 
          }
        }
      }) 

      let url = UPDATE_PRICES_ENDPOINT.replace(':code', simulationData.results && simulationData.results.code)
      try {
        // Omit invalid
        delete form.invalid
        const params = { ...form }
        const patch = yield call(axios.patch, url, params)
        
        yield put(setSimulationResults(csObjectFromUnderscore(patch.data)))
        yield put(toggleEditPricesModal({ opened: false }))
      } catch (error) {
        yield put(setError(error))
        console.log(error)
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/containers/Results/logic.js